import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Session } from './utils/session';
import { fetchUsersListCached } from '../utils/usersListCache';

const MAX_VISIBLE_OPTIONS = 60;

function normalizeUser(u) {
  const userName = u.userName ?? u.UserName ?? '';
  const firstName = u.firstName ?? u.FirstName ?? '';
  const lastName = u.lastName ?? u.LastName ?? '';
  const label = `${firstName} ${lastName}`.trim() || userName;
  return { userId: u.userId ?? u.UserId, userName, label };
}

export default function SearchableUserSelect({
  value,
  onChange,
  onDraftChange,
  placeholder = 'Search username',
  className = '',
  onKeyDown,
}) {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState(value || '');
  const containerRef = useRef(null);

  useEffect(() => {
    setSearch(value || '');
  }, [value]);

  useEffect(() => {
    const token = Session.getToken();
    if (!token) return;

    fetchUsersListCached()
      .then((data) => {
        setUsers(
          (Array.isArray(data) ? data : [])
            .map(normalizeUser)
            .filter((u) => u.userName)
            .sort((a, b) => a.userName.localeCompare(b.userName, undefined, { sensitivity: 'base' }))
        );
      })
      .catch(() => setUsers([]));
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => {
      const name = u.userName.toLowerCase();
      const label = u.label.toLowerCase();
      return name.includes(q) || label.includes(q);
    });
  }, [users, search]);

  const visibleOptions = useMemo(
    () => filtered.slice(0, MAX_VISIBLE_OPTIONS),
    [filtered]
  );

  useEffect(() => {
    const onDocClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const handleSelect = (userName) => {
    onChange(userName);
    onDraftChange?.(userName);
    setSearch(userName);
    setOpen(false);
  };

  const handleInputChange = (e) => {
    const v = e.target.value;
    setSearch(v);
    setOpen(true);
    // Keep typing local so the heavy parent page does not re-render every keystroke.
    onDraftChange?.(v);
  };

  const handleClear = () => {
    onChange('');
    onDraftChange?.('');
    setSearch('');
    setOpen(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      onChange(search);
      onDraftChange?.(search);
    }
    onKeyDown?.(e);
  };

  return (
    <div className={`searchable-user-select ${className}`.trim()} ref={containerRef}>
      <div className="searchable-user-select-input-wrap">
        <input
          type="text"
          className="form-control reportsInput searchable-user-select-input"
          placeholder={placeholder}
          value={search}
          onChange={handleInputChange}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          autoComplete="off"
        />
        {(value || search) && (
          <button
            type="button"
            className="searchable-user-select-clear"
            onClick={handleClear}
            aria-label="Clear employee"
          >
            &times;
          </button>
        )}
      </div>
      {open && (
        <div className="searchable-user-select-dropdown">
          <button
            type="button"
            className={`searchable-user-select-option${!value && !search.trim() ? ' active' : ''}`}
            onClick={() => handleSelect('')}
          >
            All employees
          </button>
          {filtered.length === 0 ? (
            <div className="searchable-user-select-empty">No users found</div>
          ) : (
            <>
              {visibleOptions.map((u) => (
                <button
                  key={u.userId || u.userName}
                  type="button"
                  className={`searchable-user-select-option${value === u.userName ? ' active' : ''}`}
                  onClick={() => handleSelect(u.userName)}
                >
                  <span className="searchable-user-select-username">{u.userName}</span>
                  {u.label !== u.userName && (
                    <span className="searchable-user-select-label">{u.label}</span>
                  )}
                </button>
              ))}
              {filtered.length > MAX_VISIBLE_OPTIONS && (
                <div className="searchable-user-select-empty">
                  Showing {MAX_VISIBLE_OPTIONS} of {filtered.length}. Type more to narrow results.
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
